"use client";

import { ResultBanner, type Verdict } from "@/components/tools/ui";
import {
  countFindings,
  type CheckResult,
  type Severity,
  type VerdictKey,
} from "@/lib/ramsCheck";

/* The scorecard rendering shared by the RAMS rejection checker tool and the
   in-builder review: verdict banner, finding counts, the eight dimension cards
   with 5-segment score bars and severity-ranked findings, suggested revision
   notes and the limitation note. The print-area wrapper stays in each caller —
   this component only renders the result body. */

const VERDICT_MAP: Record<VerdictKey, { level: Verdict; title: string }> = {
  likely_accepted: { level: "low", title: "Likely to be accepted" },
  minor_revisions_needed: { level: "medium", title: "Minor revisions needed" },
  likely_rejected: { level: "high", title: "Likely to be rejected" },
  not_a_rams_document: { level: "medium", title: "This doesn't read as a RAMS" },
};

const SEVERITY_ORDER: Record<Severity, number> = { critical: 0, major: 1, minor: 2 };

const SEVERITY_CHIP: Record<Severity, string> = {
  critical: "bg-[#fdecec] text-[#a31616]",
  major: "bg-[#fdf6e3] text-[#8a6400]",
  minor: "bg-mist text-muted",
};

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
};

/** 0–1 red, 2–3 amber, 4–5 green. */
function scoreColour(score: number): string {
  if (score <= 1) return "bg-risk-veryhigh";
  if (score <= 3) return "bg-risk-medium";
  return "bg-risk-low";
}

export function CheckResultView({ result }: { result: CheckResult }) {
  const { level, title } = VERDICT_MAP[result.verdict];
  const counts = countFindings(result.dimensions);
  const isNotRams = result.verdict === "not_a_rams_document";
  const countLine = `${counts.critical} critical · ${counts.major} major · ${counts.minor} minor`;

  return (
    <>
      <ResultBanner level={level} title={title}>
        <p>{result.summary}</p>
        {!isNotRams && <p className="mt-1 font-semibold">{countLine}</p>}
      </ResultBanner>

      {!isNotRams && (
        <>
          <div className="mt-5 space-y-4">
            {result.dimensions.map((dim) => {
              const findings = [...dim.findings].sort(
                (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
              );
              return (
                <div key={dim.key} className="avoid-break rounded-xl border border-line p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-base font-semibold text-ink">{dim.name}</h3>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="flex gap-0.5" aria-hidden>
                        {[0, 1, 2, 3, 4].map((seg) => (
                          <span
                            key={seg}
                            className={`h-2 w-4 rounded-sm ${
                              seg < dim.score ? scoreColour(dim.score) : "bg-mist"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-[0.82rem] font-bold text-ink">{dim.score}/5</span>
                    </div>
                  </div>

                  {findings.length > 0 ? (
                    <ul className="mt-3 space-y-2.5">
                      {findings.map((f, i) => (
                        <li key={i} className="text-sm">
                          <span
                            className={`mr-2 inline-block rounded px-1.5 py-0.5 align-middle text-[0.64rem] font-bold uppercase tracking-wide ${SEVERITY_CHIP[f.severity]}`}
                          >
                            {SEVERITY_LABEL[f.severity]}
                          </span>
                          <span className="font-semibold text-ink">{f.issue}</span>
                          <span className="mt-0.5 block leading-relaxed text-muted">
                            <span className="font-semibold text-body">Fix:</span> {f.fix}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-emerald-deep">No issues found on this check.</p>
                  )}
                </div>
              );
            })}
          </div>

          {result.revision_notes.length > 0 && (
            <div className="mt-5 avoid-break">
              <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">
                Suggested revision notes
              </h3>
              <p className="mt-1 text-sm text-muted">
                Drop these into the document&apos;s revision history so the reviewer can see what changed
                and why.
              </p>
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-body">
                {result.revision_notes.map((note, i) => (
                  <li key={i} className="leading-relaxed">
                    {note}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}

      <p className="mt-5 rounded-lg bg-mist px-3 py-2 text-[0.8rem] leading-relaxed text-muted">
        This is a pre-submission rehearsal, not certification. Acceptance is always the principal
        contractor&apos;s decision — have your competent person make the final call.
      </p>
    </>
  );
}
